import React from 'react';
import { first, keys } from 'lodash';
import styled from 'styled-components';

import { BoardState } from '../containers/BoardState';
import { BoardTransformer } from './BoardTransformer';
import { useParams, Redirect, Link } from 'react-router-dom';
import { hydrateState } from '../helpers/gameReducer';
import { GameProviders } from './GameProviders';
import { Button, LinkButton } from './Button';
import { useGetUser, useSubmittedBoards, useWinner } from '../hooks';
import { rejectBoard, approveBoard } from '../helpers/gameServer';
import { Container, H2, H1 } from './Layout';

const BoardContainer = styled.div`
    display: flex;
    min-height: 0;
    min-width: 0;
    flex: 1;
    position: relative;
    height: 100%;
    overflow: hidden;
`;

const NameBanner = styled.div`
    z-index: 1;
    position: absolute;
    top: 0;
    width: 100%;
    background-color: ${(props) => props.theme.colors.lightYellow};
    padding: 10px;
`;

const Controls = styled.div`
    display: flex;
    justify-content: space-between;
    z-index: 1;
    position: absolute;
    bottom: 0;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.75);
    padding: 10px;
`;

const WinnerBanner = styled.div`
    z-index: 1;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 75%;
    left: 0;
    right: 0;
    margin: auto;
`;

const ReviewWithoutProviders: React.FC = () => {
    const { dispatch } = BoardState.useContainer();
    const { id: roomId, userId } = useParams();
    const { success, submittedBoards } = useSubmittedBoards(roomId);
    const { winner } = useWinner(roomId);
    const submittingUser = first(keys(submittedBoards));
    const { user } = useGetUser(roomId, userId);

    React.useEffect(() => {
        if (success) {
            if (submittingUser && submittedBoards) {
                const board = submittedBoards[submittingUser];
                dispatch(
                    hydrateState({
                        hand: [],
                        board,
                    }),
                );
            }
        }
    }, [dispatch, submittingUser, submittedBoards, success]);

    const handleApprove = () => {
        approveBoard({ roomId, userId });
    };

    const handleReject = () => {
        rejectBoard({ roomId });
    };

    if (success && !submittingUser) {
        return <Redirect to={`/game/${roomId}`} />;
    }

    return (
        <BoardContainer>
            {!!user && (
                <NameBanner>
                    <H1>{user.userName}'s board</H1>
                </NameBanner>
            )}
            {!!winner && (
                <WinnerBanner>
                    <Container>
                        <H2>{winner.userName} has won!</H2>
                        <br />
                        <LinkButton fullWidth={true} to="/game">
                            New Game
                        </LinkButton>
                    </Container>
                </WinnerBanner>
            )}
            <BoardTransformer />
            {!winner && (
                <Controls>
                    <Button onClick={handleApprove}>Approve</Button>
                    <Button onClick={handleReject}>Reject</Button>
                </Controls>
            )}
        </BoardContainer>
    );
};

export const Review: React.FC = () => {
    return (
        <GameProviders key="review">
            <ReviewWithoutProviders />
        </GameProviders>
    );
};

